import type { QueryClient, QueryKey } from '@tanstack/react-query'
import type { FeedNewItemsItem } from 'api/api'

type Broadcast = { now?: { playQuestId?: string; author?: FeedNewItemsItem['user'] } } | null

// The existing feed badge consumes the station's explicit current-post ID.
// Reconcile again after query success so a song arriving during a page load
// cannot be lost. All refreshes go through the panel's normal refresh handler.
export function followRadioStream({
    feed,
    client,
    queryKey,
    isAtTop,
    refresh,
    onNewItems,
}: {
    feed: {
        subscribe: (
            listener: (live: Broadcast) => void,
            options: { listening: boolean },
        ) => () => void
    }
    client: QueryClient
    queryKey: QueryKey
    isAtTop: () => boolean
    refresh: () => unknown
    onNewItems: (items: FeedNewItemsItem[]) => void
}) {
    let live: Broadcast = null
    let stopped = false
    const key = JSON.stringify(queryKey)
    const reconcile = () => {
        if (stopped) return
        const id = live?.now?.playQuestId
        const data = client.getQueryData<{ pages?: { quests?: { id: unknown }[] }[] }>(queryKey)
        const present = data?.pages?.some(page =>
            page.quests?.some(post => String(post.id) === String(id)),
        )
        onNewItems(id && !present ? [{ id: String(id), user: live?.now?.author }] : [])
        if (
            id &&
            String(data?.pages?.[0]?.quests?.[0]?.id) !== String(id) &&
            isAtTop() &&
            client.getQueryState(queryKey)?.fetchStatus !== 'fetching'
        )
            refresh()
    }
    const stopQuery = client.getQueryCache().subscribe(event => {
        if (
            event.type === 'updated' &&
            event.action.type === 'success' &&
            JSON.stringify(event.query.queryKey) === key
        ) {
            // Let the completed request release its in-flight promise first.
            queueMicrotask(reconcile)
        }
    })
    const stopFeed = feed.subscribe(
        snapshot => {
            if (!snapshot) return
            live = snapshot
            reconcile()
        },
        { listening: true },
    )
    return () => {
        stopped = true
        stopFeed()
        stopQuery()
    }
}
